import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import awsmobile from '../../aws-exports';
import { Auth, CognitoUser } from '@aws-amplify/auth';

import { environment } from '../../environments/environment';

import {
  isProductActive,
  isUserAllowedToAccessToProduct,
} from '../constants/constants';
import { demoProducts } from './demo-products';
import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import moment from 'moment';
import { Moment } from 'moment';
import rg4js from 'raygun4js';
import { Observable } from 'rxjs';
import { isNil } from 'src/utils/utils';

export interface Customer {
  customerId: number;
  customerName: string;
}

export interface SeasonDtg {
  month: string;
  day: string;
}

export interface Season {
  start: SeasonDtg;
  end: SeasonDtg;
}

@Injectable()
export class AuthService {
  // TODO: On app init set all localStorage existing values to signals
  public router = inject(Router);
  private http = inject(HttpClient);
  private translate = inject(TranslateService);

  private idToken = signal<string>(undefined);
  private raygunUserData = signal<any>(undefined);
  private userProfile = signal<any>(undefined);
  private language = signal<string>(undefined);
  private customerDocumentFromBFF = signal<string>('{}');
  private customerDocument = signal<any>(undefined);
  private userCustomerDocument = signal<any>(undefined);
  private beCustomer = signal<any>(undefined);
  private kioskDisplayLogin = signal<boolean>(false);
  private updateUserDataQueue: Promise<void> = Promise.resolve();

  public loginIsRunning = signal(false);
  public isAuthenticated = signal(false);
  public isTryingAutologin = signal(false);
  private isChecking = false;
  private customerId: string;
  public hasUserAdminPermissions = signal(false);
  private beYourselfAgainCustomerDocument: any;
  public customerList: any[];
  private isUserAdminMode = false;
  private showRouteFcst4All = false;
  public isAppWebView = signal(false);
  private isBecomingAnotherFlutterCustomer = false;
  private customerHasLogbook: boolean;

  authenticated = computed(() => {
    if (this.loginIsRunning() || this.isTryingAutologin()) {
      return false;
    }
    return this.isAuthenticated();
  });

  isMaster = computed(() => {
    const userCustomerDocument = this.userCustomerDocument();
    return !!userCustomerDocument?.['isMaster'];
  });

  getCustomerAlias = computed(() => {
    const current = this.beCustomer();
    if (!isNil(current)) return current;

    const raw = localStorage.getItem('be_customer');
    return !isNil(raw) ? JSON.parse(raw) : null;
  });

  getCustomerDocument = computed(() => {
    const current = this.customerDocument();
    if (!isNil(current)) return current;

    const raw = localStorage.getItem('customer_document');
    return !isNil(raw) ? JSON.parse(raw) : null;
  });

  getUserProfile = computed(() => {
    const current = this.userProfile();
    if (!isNil(current)) return current;

    const raw = localStorage.getItem('user_profile');
    return !isNil(raw) ? JSON.parse(raw) : null;
  });

  hasUserWebAccess = computed(() => {
    const userProfile = this.getUserProfile();
    if (!userProfile || !userProfile.hasOwnProperty('metadata')) {
      return false;
    }
    const userMetaData = userProfile['metadata'];
    if (!userMetaData) {
      return true;
    }
    if (userMetaData.hasOwnProperty('active') && !userMetaData['active']) {
      return false;
    }
    // global active, start, end
    let isGlobalAccess = false;
    if (userMetaData.hasOwnProperty('start')) {
      if (moment(userMetaData['start']).isBefore(moment())) {
        if (userMetaData.hasOwnProperty('end')) {
          if (!moment(userMetaData['end']).isValid()) {
            isGlobalAccess = true;
          } else if (
            moment(userMetaData['end']).isAfter(moment().subtract(1, 'day'))
          ) {
            isGlobalAccess = true;
          }
        }
      }
    } else {
      isGlobalAccess = true;
    }
    if (!isGlobalAccess) {
      return false;
    }

    // online active, start, end
    if (userMetaData.hasOwnProperty('online')) {
      if (
        userMetaData['online'].hasOwnProperty('active') &&
        userMetaData['online']['active']
      ) {
        if (userMetaData['online'].hasOwnProperty('start')) {
          if (moment(userMetaData['online']['start']).isBefore(moment())) {
            if (userMetaData['online'].hasOwnProperty('end')) {
              if (!moment(userMetaData['online']['end']).isValid()) {
                return true;
              } else if (
                moment(userMetaData['online']['end']).isAfter(
                  moment().subtract(1, 'day')
                )
              ) {
                return true;
              }
            }
          }
        }
      }
      return false;
    }
    return true;
  });

  static isMaster(payload): boolean {
    if (payload.hasOwnProperty('roles_einsatzwetter')) {
      return (
        payload['roles_einsatzwetter'].indexOf('read') > -1 &&
        payload['roles_einsatzwetter'].indexOf('write') > -1 &&
        payload['roles_einsatzwetter'].indexOf('admin') > -1
      );
    }
    return false;
  }

  static getLanguageFromBrowserSettings(): string {
    let language = 'de';
    if (window.navigator.language.indexOf('en') >= 0) {
      language = 'us';
    } else if (window.navigator.language.indexOf('nl') >= 0) {
      language = 'nl';
    } else if (window.navigator.language.indexOf('fr') >= 0) {
      language = 'fr';
    } else if (window.navigator.language.indexOf('es') >= 0) {
      language = 'es';
    }
    return language;
  }

  static isSeason(season: Season): boolean {
    // convert to moment
    const today: Moment = moment(moment().format('YYYY-MM-DD 00:00:00'));
    const start: Moment = moment({
      year: today.year(),
      month: Number(season.start.month) - 1,
      day: Number(season.start.day),
    });
    const end: Moment = moment({
      year: today.year(),
      month: Number(season.end.month) - 1,
      day: Number(season.end.day),
    });

    if (start.isSameOrBefore(end)) {
      // start <= end, z.B. 01.04.-30.10.
      return !(today.isBefore(start) || today.isAfter(end));
    }
    // start > end, z.B. 01.11.-30.04.
    if (today.isSameOrAfter(start) || today.isSameOrBefore(end)) {
      // z.B. today = 05.12. || today = 24.03
      return true;
    }
    return false;
  }

  public isDateInSeason(day: Moment, season: Season): boolean {
    if (!season) {
      return true;
    }
    // convert to moment
    const start: Moment = moment({
      year: day.year(),
      month: Number(season.start.month) - 1,
      day: Number(season.start.day),
    });
    const end: Moment = moment({
      year: day.year(),
      month: Number(season.end.month) - 1,
      day: Number(season.end.day),
    });

    if (start.isSameOrBefore(end)) {
      // start <= end, z.B. 01.04.-30.10.
      return !(day.isBefore(start) || day.isAfter(end));
    }
    // start > end, z.B. 01.11.-30.04.
    if (day.isSameOrAfter(start) || day.isSameOrBefore(end)) {
      // z.B. today = 05.12. || today = 24.03
      return true;
    }
    return false;
  }

  public handleAuthentication(
    user: CognitoUser,
    isKioskDisplayLogin = false
  ): void {
    // const accessToken = user.getSignInUserSession().getAccessToken();
    const cognitoToken = user.getSignInUserSession().getIdToken();

    const idToken = cognitoToken.getJwtToken();
    this.setIdToken(idToken);
    this.setKioskDisplayLogin(isKioskDisplayLogin);

    const payload = cognitoToken.payload;
    if (
      payload.hasOwnProperty('customerId') &&
      payload.hasOwnProperty('roles_einsatzwetter') &&
      payload['roles_einsatzwetter'].indexOf('read') > -1
    ) {
      this.customerId = payload['customerId'];
      if (environment.production) {
        const raygunUserData = {
          identifier: payload['email'],
          isAnonymous: false,
          email: payload['email'],
          firstName: 'Foo',
          fullName: this.customerId,
          uuid: user.getUsername(),
        };
        this.setRaygunUserData(raygunUserData);
        rg4js('setUser', raygunUserData);
      }
      this.fetchCustomerDocumentFromBff(Number(this.customerId)).subscribe(
        data => {
          this.isAuthenticated.set(true);
          this.setCustomerDocumentFromBFF(data);
          this.getUserAdminPermissions(payload);
          this.setCustomerDocument({
            ...data,
            ['isMaster']: AuthService.isMaster(payload),
            ['userId']: payload.userId,
            ['name']: payload['email'],
          });

          this.fetchUserProfileFromBff(payload.userId).subscribe(
            userdata => {
              this.setUserProfile(userdata);
              this.setLanguage(true);
              if (this.customerDocument()['isMaster']) {
                this.addDemoProducts();
              } else if (
                this.showRouteFcst4All ||
                this.customerId === '10839'
              ) {
                this.addDemoProducts(['routeProducts']);
              }
              this.beYourselfAgainCustomerDocument = this.customerDocument();
              this.setUserCustomerDocument(this.customerDocument());
              this.router.navigate(['/']).then(() => {
                this.loginIsRunning.set(false);
              });
            },
            error =>
              console.log(
                'Error AuthService handleAuthentication fetchUserProfileFromBff',
                error
              )
          );
        },
        error1 =>
          console.log(
            'Error AuthService handleAuthentication fetchUserProfileFromBff',
            error1
          )
      );
    }
  }

  initLocalStorageValues() {
    this.idToken.set(localStorage.getItem('idToken'));

    const raygunUserData = localStorage.getItem('raygun_userdata');
    if (!isNil(raygunUserData)) {
      this.raygunUserData.set({ ...JSON.parse(raygunUserData) });
    }

    const userProfile = localStorage.getItem('user_profile');
    if (!isNil(userProfile)) {
      this.userProfile.set({ ...JSON.parse(userProfile) });
    }

    const kioskDisplayLogin = localStorage.getItem('kiosk_display_login');
    if (!isNil(kioskDisplayLogin)) {
      this.kioskDisplayLogin.set(JSON.parse(kioskDisplayLogin));
    }

    this.setCurrentLanguage(localStorage.getItem('language'));

    this.customerDocumentFromBFF.set(
      localStorage.getItem('customerDocumentFromBFF')
    );

    const customerDocument = localStorage.getItem('customer_document');
    if (!isNil(customerDocument)) {
      this.customerDocument.set({ ...JSON.parse(customerDocument) });
    }

    const userCustomerDocument = localStorage.getItem('user_customer_document');
    if (!isNil(userCustomerDocument)) {
      this.userCustomerDocument.set({ ...JSON.parse(userCustomerDocument) });
    }

    const beCustomer = localStorage.getItem('be_customer');
    if (!isNil(beCustomer)) {
      this.beCustomer.set({ ...JSON.parse(beCustomer) });
    }
  }

  addDemoProducts(singleProducts = []): void {
    const allDemoProducts = [
      'routeProducts',
      'eppProducts',
      'smwProducts',
      'egpSummerProducts',
      'egwProducts',
      'egwSummerProducts',
      'marwisProducts',
      'radarProducts',
      'egpProducts',
      'egwGmaProducts',
      'trafficLightsProducts',
      'pigeonProducts',
      'satelliteProducts',
      'modelProducts',
    ];
    const productTypes =
      singleProducts.length > 0 ? singleProducts : allDemoProducts;
    for (const productType of productTypes) {
      if (
        !this.customerDocument().hasOwnProperty(productType) ||
        this.customerDocument()[productType].length === 0 ||
        (singleProducts.length > 0 && !this.isProductActive(productType))
      ) {
        if (
          (productType === 'egpSummerProducts' &&
            this.customerDocument()['egpProducts'].length > 0) ||
          (productType === 'egpProducts' &&
            this.customerDocument()['egpSummerProducts'].length > 0)
        ) {
          continue;
        }
        this.setCustomerDocument({
          ...this.customerDocument(),
          [productType]: demoProducts[productType],
        });
      }
    }
  }

  // Builds a CognitoUser object from the token response (same shape for both
  // GET /app/token and POST /app/token) and mirrors the tokens into localStorage
  // so the rest of Amplify stays in sync regardless of this flow.
  private buildCognitoUserFromTokenResponse(data: unknown): CognitoUser | null {
    if (
      data === null ||
      data === undefined ||
      !Object.prototype.hasOwnProperty.call(data, 'Username')
    ) {
      return null;
    }

    const poolData = {
      UserPoolId: awsmobile.aws_user_pools_id, // Use your real test-system keys here
      ClientId: awsmobile.aws_user_pools_web_client_id,
    };
    const username = data['Username'];
    const userPool = new CognitoUserPool(poolData);

    const idToken = new CognitoIdToken({ IdToken: data['IdToken'] });
    const accessToken = new CognitoAccessToken({
      AccessToken: data['AccessToken'],
    });
    const refreshToken = new CognitoRefreshToken({
      RefreshToken: data['RefreshToken'],
    });

    const session = new CognitoUserSession({
      IdToken: idToken,
      AccessToken: accessToken,
      RefreshToken: refreshToken,
    });

    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    // Injects the session directly into the object - entirely independent of Amplify's own state
    cognitoUser.setSignInUserSession(session);

    // Mirror into localStorage in parallel, so the rest of Amplify knows about it
    const prefix = `CognitoIdentityServiceProvider.${poolData.ClientId}.${username}`;
    const lastUserKey = `CognitoIdentityServiceProvider.${poolData.ClientId}.LastAuthUser`;

    localStorage.setItem(lastUserKey, username);
    localStorage.setItem(`${prefix}.idToken`, data['IdToken']);
    localStorage.setItem(`${prefix}.accessToken`, data['AccessToken']);
    localStorage.setItem(`${prefix}.refreshToken`, data['RefreshToken']);

    return cognitoUser;
  }

  public async automaticCredentials(): Promise<CognitoUser> {
    return new Promise<CognitoUser>((resolve, reject) => {
      const url = environment.url.bffV3 + '/app/token';
      this.http.get(url).subscribe(data => {
        const cognitoUser = this.buildCognitoUserFromTokenResponse(data);
        if (cognitoUser) {
          resolve(cognitoUser);
        }
      });
    });
  }

  // Generalized variant of the pilot: resolves a customer-bound code
  // (from the confidentiality-agreement link) into a token on the backend.
  public async automaticCredentialsByCode(code: string): Promise<CognitoUser> {
    return new Promise<CognitoUser>((resolve, reject) => {
      const url = environment.url.bffV3 + '/app/token';
      this.http
        .post(url, JSON.stringify(code), {
          headers: { 'Content-Type': 'application/json' },
        })
        .subscribe({
          next: data => {
            const cognitoUser = this.buildCognitoUserFromTokenResponse(data);
            if (cognitoUser) {
              resolve(cognitoUser);
            } else {
              reject(new Error('Invalid token response for code login'));
            }
          },
          error: err => reject(err),
        });
    });
  }

  async checkFlutterCredentials() {
    const flutterAccessToken = localStorage.getItem('flutterAccessToken');
    if (flutterAccessToken !== null) {
      this.isAppWebView.set(true);
      if (!this.isBecomingAnotherFlutterCustomer) {
        const customerDocument = this.getCustomerDocument();
        if (customerDocument !== null) {
          const customerId = customerDocument.customerId;
          const flutterCustomerId = localStorage.getItem('customerId');
          if (flutterCustomerId != customerId) {
            const flutterBeCustomer = localStorage.getItem('flutterBeCustomer');
            if (isNil(flutterBeCustomer)) {
              this.becomeYourself();
            } else {
              this.isBecomingAnotherFlutterCustomer = true;

              const customerDocument = localStorage.getItem(
                'flutterCustomerDocument'
              );

              this.setCustomerDocument(customerDocument);
              if (!isNil(customerDocument)) {
                const parsedCustomerDocument = JSON.parse(customerDocument);
                this.setCustomerDocument(parsedCustomerDocument);
              } else {
                this.setCustomerDocument(null);
              }

              this.isBecomingAnotherFlutterCustomer = false;
            }
          }
        }
      }
    }
  }

  public async initAuth(): Promise<void> {
    return new Promise<void>(resolve => {
      if (
        !this.isAuthenticated() &&
        !this.loginIsRunning() &&
        !this.isTryingAutologin() //&&
        //!this.isChecking
      ) {
        this.isChecking = true;
        this.checkFlutterCredentials().then(() => {
          const savedToken = localStorage.getItem('idToken');
          Auth.currentSession()
            .then(session => {
              const currentToken = session.getIdToken().getJwtToken();
              if (savedToken !== currentToken) {
                this.setIdToken(currentToken);
              }
              this.isAuthenticated.set(true);
              const payload = session.getIdToken().payload;
              this.getUserAdminPermissions(payload);
              this.isChecking = false;
              resolve();
            })
            .catch(err => {
              this.isAuthenticated.set(false);
              this.tryLoginWithToken().then(() => {
                resolve();
              });
            });
        });
      } else {
        this.isChecking = false;
        resolve();
      }
    });
  }

  async tryLoginWithToken(): Promise<void> {
    if (!this.isTryingAutologin()) {
      const idToken = localStorage.getItem('flutterIdToken');
      if (idToken === null) return;
      this.isTryingAutologin.set(true);
      const userPoolId = awsmobile.aws_user_pools_id;
      const clientId = awsmobile.aws_user_pools_web_client_id;
      const accessToken = localStorage.getItem('flutterAccessToken'); // Token aus dem Local Storage abrufen
      if (accessToken === null) {
        this.isTryingAutologin.set(false);
        this.isAuthenticated.set(false);
        this.isChecking = false;
        return;
      }
      const refreshToken = localStorage.getItem('flutterRefreshToken');
      const username = localStorage.getItem('flutterCognitoUser');
      const customerId = localStorage.getItem('customerId');
      const aliasCustomer = localStorage.getItem('flutterBeCustomer');
      const customerDocument = JSON.parse(
        localStorage.getItem('flutterCustomerDocument')
      );

      const userPool = new CognitoUserPool({
        UserPoolId: userPoolId,
        ClientId: clientId,
      });
      const cognitoIdToken = new CognitoIdToken({
        IdToken: idToken,
      });
      const cognitoAccessToken = new CognitoAccessToken({
        AccessToken: accessToken,
      });
      const cognitoRefreshToken = new CognitoRefreshToken({
        RefreshToken: refreshToken,
      });
      const user = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      user.setSignInUserSession(
        new CognitoUserSession({
          AccessToken: cognitoAccessToken,
          IdToken: cognitoIdToken,
          RefreshToken: cognitoRefreshToken,
        })
      );
      this.customerId = customerId;
      const payload = cognitoIdToken.payload;
      this.getUserAdminPermissions(payload);
      customerDocument['isMaster'] =
        aliasCustomer !== null ? false : AuthService.isMaster(payload);
      customerDocument['userId'] = payload.userId;
      customerDocument['name'] = payload['email'];
      customerDocument['customerId'] = Number(customerId);
      this.setCustomerDocument(customerDocument);

      if (this.customerDocument()['isMaster']) {
        this.addDemoProducts();
      } else if (this.showRouteFcst4All) {
        this.addDemoProducts(['routeProducts']);
      }

      this.setIdToken(cognitoIdToken.getJwtToken());
      this.setBeCustomer(null);

      this.isTryingAutologin.set(false);
      this.isAuthenticated.set(true);
      this.isChecking = false;
    }
  }

  async logout(): Promise<void> {
    try {
      this.setBeCustomer(null);
      localStorage.clear();
      sessionStorage.clear();
      this.isAuthenticated.set(false);
      this.setCustomerDocument(null);
      this.setUserProfile(null);
      await Auth.signOut();
      location.href = environment.afterLogoutUrl[this.getLanguage()];
    } catch (error) {
      console.log('error signing out: ', error);
    }
  }

  public beAnotherCustomer(customer: Customer): Promise<void> {
    return new Promise((resolve, reject) => {
      this.fetchCustomerDocumentFromBff(customer.customerId).subscribe(
        customerDocument => {
          this.setCustomerDocumentFromBFF(customerDocument);
          this.customerId = customer.customerId + '';
          const userId = this.getCustomerDocument()['userId'];
          this.setCustomerDocument({
            ...customerDocument,
            ['isMaster']: false,
            ['userId']: userId,
            ['name']: customer.customerName,
          });
          if (this.showRouteFcst4All || this.customerId === '10839') {
            this.addDemoProducts(['routeProducts']);
          }
          this.setBeCustomer(customer);
          this.clearSettings();
          if (environment.production) {
            rg4js('setUser', {
              firstName: customer.customerName,
              fullName: this.customerId,
              identifier: '',
              isAnonymous: false,
              email: '',
              uuid: '',
            });
          }
          resolve();
        },
        error1 => reject(error1)
      );
    });
  }

  public refreshCustomerDocument(): void {
    const customerDocument = this.getCustomerDocument();
    const customerId = customerDocument.customerId;
    const isMaster = customerDocument['isMaster'];
    if (isMaster && this.getCustomerAlias()) {
      return;
    }
    const userId = customerDocument['userId'];
    const name = customerDocument['name'];
    if (isNil(this.customerDocumentFromBFF())) {
      const fromLocalStorage = localStorage.getItem('customerDocumentFromBFF');
      if (fromLocalStorage) {
        this.setCustomerDocumentFromBFF(fromLocalStorage);
      } else {
        this.setCustomerDocumentFromBFF('{}');
      }
    }
    this.fetchCustomerDocumentFromBff(customerId).subscribe(
      data => {
        if (JSON.stringify(data) !== this.customerDocumentFromBFF()) {
          // return;
          this.setCustomerDocumentFromBFF(data);
          this.setCustomerDocument({
            ...this.customerDocument(),
            ['isMaster']: isMaster,
            ['userId']: userId,
            ['name']: name,
          });

          if (isMaster) {
            this.addDemoProducts();
          } else if (this.showRouteFcst4All) {
            this.addDemoProducts(['routeProducts']);
          }
        }
      },
      error =>
        console.log(
          'Error AuthService refreshCustomerDocument fetchCustomerDocumentFromBff',
          error
        )
    );
  }

  clearSettings(): void {
    const settingKeys = [
      'parametersets',
      'eppColorMappings',
      'eppColorMappingNames',
    ];
    for (const settingKey of settingKeys) {
      localStorage.removeItem(settingKey);
    }
  }

  private fetchCustomerDocumentFromBff(customerId: number): Observable<any> {
    const url = environment.url.bffV3 + '/customer/' + customerId;
    return this.http.get(url);
  }

  private fetchUserProfileFromBff(userId: number): Observable<any> {
    const url = environment.url.bffV2 + '/user/1/management/' + userId;
    return this.http.get(url);
  }

  private patchUserMetaDataOnBff(
    userId: number,
    userData: any
  ): Observable<any> {
    return this.http.put(
      environment.url.bffV2 + '/user/1/management/' + userId,
      userData
    );
  }

  private getUserAdminPermissions(payload): void {
    this.hasUserAdminPermissions.set(
      payload['roles_einsatzwetter'].indexOf('customer_user_admin') > -1 ||
        payload['roles_einsatzwetter'].indexOf('admin') > -1
    );
  }

  public updateUserData(category: string, value: any): Promise<void> {
    this.updateUserDataQueue = this.updateUserDataQueue.then(async () => {
      const userId = this.getCustomerDocument()?.['userId'];
      if (userId === undefined || userId === null) {
        return;
      }

      const profile = await new Promise<any>((resolve, reject) => {
        this.fetchUserProfileFromBff(userId).subscribe({
          next: data => resolve(data),
          error: err => reject(err),
        });
      });

      const userProfile = profile ? structuredClone(profile) : {};
      if (!userProfile['metadata']) {
        userProfile['metadata'] = {};
      }

      userProfile['metadata'][category] = value;

      this.setUserProfile(userProfile);

      await new Promise<void>((resolve, reject) => {
        this.patchUserMetaDataOnBff(userId, userProfile).subscribe({
          next: () => resolve(),
          error: err => reject(err),
        });
      });
    });

    return this.updateUserDataQueue;
  }

  /*public updateUserData(category: string, value: any): Promise<void> {
    const userId = this.getCustomerDocument()['userId'];
    return new Promise<void>((resolve, reject) => {
      if (userId === undefined) {
        resolve();
        return;
      }
      this.fetchUserProfileFromBff(userId).subscribe(
        data => {
          const userProfile = data;
          userProfile['metadata'][category] = value;
          this.setUserProfile(userProfile);
          console.debug('[AuthService] PUT user profile payload preview', {
            userId,
            metadataKeys: Object.keys(userProfile.metadata ?? {}),
            globalKeys: Object.keys(userProfile.metadata?.global ?? {}),
            payloadLength: JSON.stringify(userProfile).length,
          });
          this.patchUserMetaDataOnBff(userId, userProfile).subscribe(
            data1 => {
              resolve();
            },
            error1 => reject(error1)
          );
        },
        error2 => reject(error2)
      );
    });
  }
*/
  public becomeYourself(): void {
    this.setBeCustomer(null);

    if (!this.beYourselfAgainCustomerDocument) {
      if (localStorage.getItem('user_customer_document')) {
        this.beYourselfAgainCustomerDocument = JSON.parse(
          localStorage.getItem('user_customer_document')
        );
      }
    }
    this.setCustomerDocument(
      isNil(this.beYourselfAgainCustomerDocument)
        ? null
        : { ...this.beYourselfAgainCustomerDocument }
    );
    this.setBeCustomer(null);
    this.clearSettings();
  }

  public getCustomerList(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      if (!this.customerList) {
        // cache
        const customerList = null; // this.cacheService.getCachedData('customerlist');
        if (customerList) {
          this.customerList = customerList;
          resolve(this.customerList);
        } else {
          // api
          this.getCustomerListFromBff().subscribe(
            data => {
              this.customerList = data['customers'];
              // this.cacheService.setCacheData('customerlist', data, 30);
              resolve(this.customerList);
            },
            error => {
              console.log('auth getCustomerList - error', error);
              // this.cacheService.setCacheData('customerlist', [], 30);
              reject();
            }
          );
        }
      } else {
        // already stored in service
        resolve(this.customerList);
      }
    });
  }

  private getCustomerListFromBff(): Observable<any> {
    const url = environment.url.bff + '/customer/list';
    return this.http.get(url);
  }

  public getLanguage(): string {
    let language = localStorage.getItem('language');
    if (isNil(language)) {
      this.setLanguage();
      language = localStorage.getItem('language');
    }
    return language;
  }

  public setLanguage(fromUserProfile: boolean = false): void {
    this.translate.addLangs(['us', 'de', 'nl', 'fr', 'es', 'es-ct', 'ch-de']);
    this.translate.setDefaultLang('de');
    let language: string;
    if (this.authenticated() || fromUserProfile) {
      const userProfile = this.getUserProfile();
      if (
        userProfile['metadata'].hasOwnProperty('global') &&
        userProfile['metadata']['global'] &&
        userProfile['metadata']['global'].hasOwnProperty('language')
      ) {
        language = userProfile['metadata']['global']['language'];
      } else {
        language = AuthService.getLanguageFromBrowserSettings();
      }
    } else {
      const storedLanguage = localStorage.getItem('language');
      if (storedLanguage) {
        language = storedLanguage;
      } else {
        language = AuthService.getLanguageFromBrowserSettings();
      }
    }
    this.setCurrentLanguage(language);
    this.translate.use(language);
    moment.locale(language);
  }

  public isProductActive(
    productType: string,
    productId?: number,
    alsoInThePast?: boolean,
    alsoOutOfSeason?: boolean,
    isUserAdminMode?: boolean
  ): boolean {
    return isProductActive(
      this.isMaster(),
      this.getCustomerAlias(),
      this.getCustomerDocument(),
      this.hasUserWebAccess(),
      this.getUserProfile()?.['metadata'],
      productType,
      productId,
      alsoInThePast,
      alsoOutOfSeason,
      isUserAdminMode
    );
  }

  public isUserAllowedToAccessToProduct(
    productId: number,
    stationId?,
    ignoreAdminMode = false,
    isUserAdminMode?: boolean
  ): boolean {
    return isUserAllowedToAccessToProduct(
      this.getUserProfile()?.['metadata'],
      productId,
      isUserAdminMode,
      stationId,
      ignoreAdminMode
    );
  }

  // Also true for any user logged in via the generalized /automatic-login
  // code flow, which should get the same kiosk UI (no menu, altered top bar)
  // as this hardcoded pilot user.
  isBambergDisplayUser = computed(() => {
    return (
      (this.userProfile() &&
        this.userProfile()['email'] ===
          'bamberg-display@wettermanufaktur.de') ||
      this.kioskDisplayLogin()
    );
  });

  isPigeonCustomer = computed(() => {
    return this.getCustomerDocument()['customerId'] === 10333;
  });

  public isZuerichCustomer(): boolean {
    return this.getCustomerDocument()['customerId'] === 10421;
  }

  public hasUserOperationReport(): boolean {
    const customerId = this.getCustomerDocument()['customerId'];
    const email = this.getUserProfile()?.['email'];

    return (
      customerId === 10211 ||
      customerId === 10951 ||
      (customerId === 10030 &&
        email === 'andreas.kalkreiber@stadtwerke-erfurt.de') ||
      (customerId === 10444 && email === 'winterdienst@tbo-offenburg.de') ||
      (customerId === 10444 && email === 'c.bruhn@wettermanufaktur.de') ||
      (customerId === 10110 && email === 'egazioch@evb-hwi.de')
    );
  }

  public async hasCustomerLogbook(): Promise<boolean> {
    return new Promise(resolve => {
      if (
        this.customerHasLogbook === undefined ||
        this.customerHasLogbook === null
      ) {
        const customerId = this.getCustomerDocument()['customerId'];
        this.fetchCustomerhasLogbook(customerId).subscribe(data => {
          this.customerHasLogbook = data.length > 0;
          resolve(this.customerHasLogbook);
        });
      } else {
        resolve(this.customerHasLogbook);
      }
    });
  }

  private fetchCustomerhasLogbook(customerId: number): Observable<any> {
    const url = environment.url.bffV3 + '/customer/' + customerId + '/logbook';
    return this.http.get(url);
  }

  private setIdToken(idToken?: string) {
    if (!isNil(idToken)) {
      localStorage.setItem('idToken', idToken);
      this.idToken.set(idToken);
    } else {
      localStorage.removeItem('idToken');
      this.idToken.set(null);
    }
  }

  private setKioskDisplayLogin(isKioskDisplayLogin: boolean) {
    localStorage.setItem(
      'kiosk_display_login',
      JSON.stringify(isKioskDisplayLogin)
    );
    this.kioskDisplayLogin.set(isKioskDisplayLogin);
  }

  private setRaygunUserData(raygunUserData: any) {
    if (!isNil(raygunUserData)) {
      localStorage.setItem('raygun_userdata', JSON.stringify(raygunUserData));
      this.raygunUserData.set({ ...raygunUserData });
    } else {
      localStorage.removeItem('raygun_userdata');
      this.raygunUserData.set(null);
    }
  }

  private setCustomerDocumentFromBFF(customerDocumentFromBFF: any) {
    if (!isNil(customerDocumentFromBFF)) {
      localStorage.setItem(
        'customerDocumentFromBFF',
        JSON.stringify(customerDocumentFromBFF)
      );
      this.customerDocumentFromBFF.set(JSON.stringify(customerDocumentFromBFF));
    } else {
      localStorage.removeItem('customerDocumentFromBFF');
      this.customerDocumentFromBFF.set(null);
    }
  }

  private setUserProfile(userProfile: any) {
    if (!isNil(userProfile)) {
      localStorage.setItem('user_profile', JSON.stringify(userProfile));
      this.userProfile.set({ ...userProfile });
    } else {
      localStorage.removeItem('user_profile');
      this.userProfile.set(null);
    }
  }

  private setCustomerDocument(customerDocument: any) {
    if (!isNil(customerDocument)) {
      localStorage.setItem(
        'customer_document',
        JSON.stringify(customerDocument)
      );
      this.customerDocument.set({
        ...customerDocument,
      });
    } else {
      localStorage.removeItem('customer_document');
      this.customerDocument.set(null);
    }
  }

  private setUserCustomerDocument(userCustomerDocument: any) {
    if (!isNil(userCustomerDocument)) {
      localStorage.setItem(
        'user_customer_document',
        JSON.stringify(userCustomerDocument)
      );
      this.userCustomerDocument.set({
        ...userCustomerDocument,
      });
    } else {
      localStorage.removeItem('user_customer_document');
      this.userCustomerDocument.set(null);
    }
  }

  private setBeCustomer(customer: any) {
    if (!isNil(customer)) {
      localStorage.setItem('be_customer', JSON.stringify(customer));
      this.beCustomer.set({ ...customer });
    } else {
      localStorage.removeItem('be_customer');
      this.beCustomer.set(null);
    }
  }

  private setCurrentLanguage(language: string) {
    if (!isNil(language)) {
      localStorage.setItem('language', language);
      this.language.set(language);
    } else {
      localStorage.removeItem('language');
      this.language.set(null);
    }
  }
}
